import {
  WSFilterApiOptionsLoader,
  WSFilterOption
} from "@wingspanhq/fe-component-library";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useMemo, useState } from "react";
import { usePayeeRowsQuery } from "../../../query/search/payee/queries/usePayeeRowsQuery";

/**
 * Custom hook for providing API-based payee filter options
 * with pagination and search support.
 *
 * @returns WSFilterApiOptionsLoader configuration for WSFilters
 *
 * @example
 * const payeeOptions = usePayeeFilterOptions();
 *
 * const filters: WSFilter[] = [
 *   {
 *     title: "Contractor",
 *     name: "payeeId",
 *     type: "select",
 *     apiOptions: payeeOptions
 *   }
 * ];
 */
export function usePayeeFilterOptions(): WSFilterApiOptionsLoader {
  const [searchText, setSearchText] = useState("");

  const query = usePayeeRowsQuery({
    filter: {
      searchString: searchText || undefined
    }
  });

  const options = useMemo(() => {
    return (query.data || [])
      .map(payee => {
        if (!payee.user) return null;

        const payeeNames = wsName({
          user: payee.user,
          member: payee.member,
          payerOwnedData: payee.payerOwnedData
        });

        const label = payeeNames.getResolvedName();
        const helperText = payeeNames.getResolvedSecondaryName();

        return {
          name: payee.payeeId,
          label,
          helperText
        };
      })
      .filter(Boolean) as WSFilterOption<"checkbox">[];
  }, [query.data]);

  return {
    data: options,
    isLoading: query.isInitialLoading,
    pagination: {
      hasNextPage: query.hasNextPage ?? false,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: query.fetchNextPage
    },
    search: {
      searchText,
      onSearchChange: setSearchText
    }
  };
}
